import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "context-screen" }
const _hoisted_2 = { class: "context-container" }
const _hoisted_3 = { class: "context-header mb-4" }
const _hoisted_4 = { class: "welcome-text" }
const _hoisted_5 = {
  key: 0,
  class: "mb-8"
}
const _hoisted_6 = { class: "context-list" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "context-card__inner" }
const _hoisted_9 = { class: "context-card__icon-wrap" }
const _hoisted_10 = { class: "context-card__content" }
const _hoisted_11 = { class: "context-card__title text-capitalize" }
const _hoisted_12 = {
  key: 1,
  class: "mb-4"
}
const _hoisted_13 = { class: "context-list" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "context-card__inner" }
const _hoisted_16 = ["src"]
const _hoisted_17 = { class: "context-card__content" }
const _hoisted_18 = { class: "context-card__title" }
const _hoisted_19 = {
  key: 0,
  class: "context-card__subtitle"
}
const _hoisted_20 = { class: "text-primary" }
const _hoisted_21 = {
  key: 0,
  class: "form-error"
}

import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAppStore } from "@/shared/stores/app";
import {
  selectContext,
  switchContext,
  selectVendorContext,
  saveAuthToken,
  saveActiveContext,
  getContextKind,
  clearPendingContextSelection,
  clearContextStorage,
  type LoginCustomer,
  type LoginContext,
  fetchProfile,
  hasProfileEnvelope,
} from "@/modules/auth/services/auth.api";
import {
  clearCustomer,
  getContexts,
  getCustomer,
  getProfileLastSynced,
  getSelectionToken,
  setCustomer,
  setProfileLastSynced,
} from "@/modules/auth/services/auth.storage";
import { isVendorMode } from "@/shared/utils/appMode";


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectContextPage',
  setup(__props) {

const router = useRouter();

const isSubmitting = ref(false);
const formError = ref("");
const selectedContextId = ref("");
function syncFromStorage() {
  contexts.value = getContexts() || [];
  customer.value = (getCustomer<LoginCustomer>() as any) || null;
}

const contexts = ref<LoginContext[]>(getContexts() || []);

const companyContexts = computed<LoginContext[]>(() => {
  return contexts.value.filter((c) => {
    if (!c) return false;
    if (c.type === "company" || c.type === "customer") return true;
    const roles = c.roles || [];
    return roles.some((r) => String(r).toLowerCase().includes("company"));
  });
});

const vendorContexts = computed<LoginContext[]>(() => {
  return contexts.value.filter((c) => {
    if (!c) return false;
    if (c.type === "vendor") return true;
    const roles = c.roles || [];
    return roles.some((r) => {
      const rr = String(r).toLowerCase();
      return rr.includes("vendor") || rr.includes("service_provider") || rr.includes("vendor_");
    });
  });
});

const customer = ref<LoginCustomer | null>((getCustomer<LoginCustomer>() as any) || null);

const customerName = computed(() => customer.value?.first_name || "User");

onMounted(async () => {
  window.addEventListener("auth-changed", syncFromStorage);

  // try to refresh profile on page load (best-effort)
  await fetchProfileAndSync();

  // if no contexts available after sync, redirect to login
  if ((contexts.value || []).length === 0) {
    clearPendingContextSelection();
    router.replace({ name: "login.page" });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("auth-changed", syncFromStorage);
});

/**
 * Role-based Visual Mapping
 */
function getRoleIcon(role: string): string {
  const r = (role || "").toString().toLowerCase();
  const mapping: Record<string, string> = {
    admin: "mdi-office-building-cog-outline",
    staff: "mdi-office-building-outline",
    company: "mdi-office-building-outline",
    vendor: "mdi-storefront",
    customer: "mdi-account-outline",
    service_provider: "mdi-briefcase-variant-outline",
  };
  return mapping[r] || "mdi-account-circle-outline";
}


function resolveContextLogo(item: LoginContext) {
  if (!item || typeof item !== "object") return "";
  if (item.type === "company") return String(item.company?.logo || "").trim();
  if (item.type === "vendor") return String(item.vendor?.logo || "").trim();
  return "";
}


/**
 * Selection Logic
 */
async function handleSelect(contextId: string) {
  selectedContextId.value = contextId;
  await handleSubmit();
}

async function fetchProfileAndSync() {
  try {
    // check last sync timestamp (ms). If synced less than 1 minute ago, skip fetch
    const last = getProfileLastSynced();
    const now = Date.now();
    if (last && now - last < 60_000) {
      // recent sync exists, skip network call
      return;
    }

    const data = await fetchProfile(); // uses auth.api.ts
    const profile = hasProfileEnvelope(data) ? data.data : (data as any);

    if (profile) {
      // prefer nested client object if present (client.first_name)
      const client = (profile && (profile.client || profile.user || profile)) as any;

      // Preserve any stored `first_login` flag (profile payloads may omit it).
      let storedFirstLogin: boolean | undefined = undefined;
      try {
        const existing = (getCustomer<any>() as any) || null;
        if (typeof existing?.first_login === "boolean") storedFirstLogin = existing.first_login;
      } catch {
        // ignore parse errors
      }

      const customerData: Partial<LoginCustomer> = {
        id: client?.id ?? client?.user_id ?? profile?.id ?? 0,
        first_name: client?.first_name ?? profile?.first_name ?? client?.fname ?? client?.name ?? "",
        last_name: client?.last_name ?? profile?.last_name ?? client?.lname ?? client?.name ?? "",
        email: client?.email ?? profile?.email ?? "",
        phone: client?.phone ?? profile?.phone ?? "",
        avatar: client?.avatar ?? profile?.avatar ?? "",
      };

      const customerForStorage: any = { ...customerData };
      const resolvedFirstLogin =
        typeof client?.first_login === "boolean"
          ? client.first_login
          : typeof storedFirstLogin === "boolean"
            ? storedFirstLogin
            : undefined;
      if (typeof resolvedFirstLogin === "boolean") {
        customerForStorage.first_login = resolvedFirstLogin;
      }
      setCustomer(customerForStorage);
      customer.value = customerForStorage as any;
      window.dispatchEvent(new Event("auth-changed"));

      // update last synced timestamp
      setProfileLastSynced(Date.now());
    }

    contexts.value = getContexts() || [];
  } catch (err) {
    // clear all related localStorage entries on error (best-effort)
    try {
      clearCustomer();
      clearPendingContextSelection();
      clearContextStorage();
    } catch {
      // ignore storage errors
    } finally {
      // notify app that auth/context changed
      window.dispatchEvent(new Event("auth-changed"));
    }
    // swallow error (best-effort)
  }
}

async function handleSubmit(){
  if(isVendorMode()) {
    await selectVendorConext(selectedContextId.value);
  } else {
    await handleDefaultSubmit();
  }
}
async function selectVendorConext(contextId: string) {
  try {
    const resp = await selectVendorContext({ context_id: contextId });
    const token = resp?.token || resp?.data?.token;
    const selectedContext =
      contexts.value.find((c) => c.context_id === contextId || c.key === contextId) || null;
    const activeContext = {
      ...(selectedContext || {}),
      ...(resp?.active_context || resp?.data?.active_context || {}),
      context_id: contextId,
    };

    saveAuthToken(token);
    saveActiveContext(activeContext);
    await fetchProfileAndSync();

    const profileSetup = (activeContext as any)?.profile_setup || null;
    if (profileSetup?.setup_required) {
      router.push({ name: "vendor.profile-setup" });
      return;
    }

    router.push({ name: "vendor.dashboard" });
  } catch (err) {
    // ignore errors (best-effort)
  }
}

async function handleDefaultSubmit() {


  if (!selectedContextId.value) return;

  formError.value = "";
  isSubmitting.value = true;

  try {
    const hasSelectionToken = Boolean(getSelectionToken());

    let response;
    if (hasSelectionToken) {
      response = await selectContext({ context_id: selectedContextId.value, is_vendor: false });
    } else {
      response = await switchContext({ context_id: selectedContextId.value });
    }

    const token = response?.data?.token;


    if (token) {
      saveAuthToken(token);
    }


    clearPendingContextSelection();

    const selectedContext =
      contexts.value.find((c) => c.context_id === selectedContextId.value) || null;
    const contextFromResponse = (response?.active_context || response?.data?.active_context || null) as
      | Partial<LoginContext>
      | null;
    const mergedContext = {
      ...(selectedContext || {}),
      ...(contextFromResponse || {}),
      context_id: selectedContextId.value,
    };

    saveActiveContext(mergedContext);

    // fetch latest profile and sync localStorage (best-effort)
    await fetchProfileAndSync();

    const profileSetup = (mergedContext as any)?.profile_setup || null;
    const setupCompleted = profileSetup?.setup_completed;
    const firstLogin = (mergedContext as any)?.first_login === true;

    // Vendor profile setup should only run when setup is NOT completed
    if (setupCompleted === false) {
      router.push({ name: "vendor.profile-setup" });
      return;
    }

    // If setup is completed (or profile_setup missing) but it's the user's first login,
    // force password change instead of taking them to profile setup.
    if (firstLogin) {
      router.push({ name: "change-password.page" });
      return;
    }

    const kind = getContextKind(mergedContext);
    let targetRoute = { name: "marketplace.home" };
    if (kind === "customer") {
      targetRoute = { name: "customer.dashboard" };
    } else if (kind === "vendor") {
      targetRoute = { name: "vendor.dashboard" };
    }

    router.push(targetRoute);


  } catch (error: any) {
    formError.value =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to select context. Please try again.";

    // Remove local storage related to contexts on error
    clearContextStorage();
  } finally {
    isSubmitting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_overlay = _resolveComponent("v-overlay")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _createElementVNode("h1", _hoisted_4, "Welcome, " + _toDisplayString(customerName.value), 1),
        _cache[1] || (_cache[1] = _createElementVNode("p", { class: "instruction-text" }, "Choose how you'd like to continue", -1))
      ]),
      (companyContexts.value.length)
        ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
            _cache[2] || (_cache[2] = _createElementVNode("p", { class: "section-title" }, "CONTINUE AS", -1)),
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(companyContexts.value, (item) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: item.context_id,
                  class: "context-card-wrapper",
                  onClick: ($event: any) => (handleSelect(item.key))
                }, [
                  _createVNode(_component_v_card, {
                    class: _normalizeClass(["context-card", { 'context-card--active': selectedContextId.value === item.context_id }]),
                    flat: ""
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                          _createVNode(_component_v_avatar, {
                            color: "primary",
                            variant: "tonal",
                            size: "40",
                            rounded: "lg"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_icon, {
                                color: "primary",
                                icon: getRoleIcon(item.type)
                              }, null, 8, ["icon"])
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("h2", _hoisted_11, _toDisplayString(item.type), 1)
                        ])
                      ])
                    ]),
                    _: 2
                  }, 1032, ["class"])
                ], 8, _hoisted_7))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      (vendorContexts.value.length)
        ? (_openBlock(), _createElementBlock("section", _hoisted_12, [
            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "section-title" }, "ASSOCIATED VENDORS", -1)),
            _createElementVNode("div", _hoisted_13, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vendorContexts.value, (item) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: item.context_id,
                  class: "context-card-wrapper",
                  onClick: ($event: any) => (handleSelect(item.key))
                }, [
                  _createVNode(_component_v_card, {
                    class: _normalizeClass(["context-card", { 'context-card--active': selectedContextId.value === item.context_id }]),
                    flat: ""
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_15, [
                        _createVNode(_component_v_avatar, {
                          variant: "tonal",
                          size: "40",
                          rounded: "lg",
                          color: "primary"
                        }, {
                          default: _withCtx(() => [
                            (resolveContextLogo(item))
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: resolveContextLogo(item),
                                  alt: "Context logo",
                                  class: "context-logo",
                                  loading: "lazy"
                                }, null, 8, _hoisted_16))
                              : (_openBlock(), _createBlock(_component_v_icon, {
                                  key: 1,
                                  icon: getRoleIcon(item.type),
                                  size: "24"
                                }, null, 8, ["icon"]))
                          ]),
                          _: 2
                        }, 1024),
                        _createElementVNode("div", _hoisted_17, [
                          _createElementVNode("h3", _hoisted_18, _toDisplayString(item.vendor?.name || item.company?.name || item.label || item.type), 1),
                          (item.roles && item.roles.length)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_19, [
                                (item.roles.length === 1)
                                  ? (_openBlock(), _createBlock(_component_v_chip, {
                                      key: 0,
                                      color: "primary",
                                      label: "",
                                      size: "small"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(item.roles[0]), 1)
                                      ]),
                                      _: 2
                                    }, 1024))
                                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                      _createVNode(_component_v_chip, {
                                        color: "primary",
                                        label: "",
                                        size: "small"
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(item.roles[0]), 1)
                                        ]),
                                        _: 2
                                      }, 1024),
                                      _createElementVNode("span", _hoisted_20, " +" + _toDisplayString(item.roles.length - 1) + " roles", 1)
                                    ], 64))
                              ]))
                            : _createCommentVNode("", true)
                        ])
                      ])
                    ]),
                    _: 2
                  }, 1032, ["class"])
                ], 8, _hoisted_14))
              }), 128))
            ])
          ]))
        : _createCommentVNode("", true),
      _createVNode(_Transition, { name: "fade" }, {
        default: _withCtx(() => [
          (formError.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_21, _toDisplayString(formError.value), 1))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_component_v_overlay, {
        modelValue: isSubmitting.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isSubmitting).value = $event)),
        class: "align-center justify-center",
        persistent: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_progress_circular, {
            color: "primary",
            indeterminate: "",
            size: "64"
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ])
  ]))
}
}

})